import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  UploadCloud,
  X,
  Upload,
  ImagePlus,
  CheckCircle,
  FileText,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  uploadImages,
  uploadMultipleTypes,
  deleteImage,
} from "../../../services/uploaderServices";

const FileUpload = forwardRef(
  (
    {
      mode = "image",
      onUpload,
      onDelete,
      defaultLabel = "Choose file",
      setImage,
      image,
      single = false,
    },
    ref
  ) => {
    const fileInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState(null);
    const [localPreviews, setLocalPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);

    const isZipMode = mode === "zip";
    const isImageMode = mode === "image";
    const isDocumentModes = mode === "document";
    const isTileMode = mode === "tile";
    const isResume = mode === "resume";
    const isMain = mode === "main";

    const isImageLikeMode = isImageMode || isTileMode || isMain;
    const isDocumentMode = isDocumentModes || isResume;

    useEffect(() => {
      if (image?.length > 0) {
        setLocalPreviews([]);
        setSelectedFiles(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    }, [image]);

    useEffect(() => {
      return () => {
        localPreviews.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [localPreviews]);

    useImperativeHandle(ref, () => ({
      reset: () => clearSelection(),
    }));

    const handleChange = (e) => {
      const files = e.target.files;
      if (!files || typeof files !== "object") {
        setSelectedFiles(null);
        return;
      }

      setSelectedFiles(files);

      if (isImageLikeMode && files.length > 0) {
        const previews = Array.from(files).map((file) =>
          URL.createObjectURL(file)
        );
        setLocalPreviews(previews);
      } else {
        setLocalPreviews([]);
      }
    };

    const triggerFileSelect = () => {
      fileInputRef.current?.click();
    };

    const clearSelection = () => {
      localPreviews.forEach((url) => URL.revokeObjectURL(url));
      setSelectedFiles(null);
      setLocalPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    };

    const handleUpload = async () => {
      if (!selectedFiles?.length) {
        toast.error("Please select a file.");
        return;
      }

      const formData = new FormData();

      setUploading(true);
      try {
        let response;

        if (isDocumentMode) {
          const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "text/plain",
          ];

          const filesArray = Array.from(selectedFiles);
          const invalidFiles = filesArray.filter(
            (file) => !allowedTypes.includes(file.type)
          );

          if (invalidFiles.length > 0) {
            toast.error(
              `Unsupported document type(s): ${invalidFiles
                .map((f) => f.name)
                .join(", ")}`
            );
            setUploading(false);
            return;
          }

          filesArray.forEach((file) => {
            formData.append("files", file);
          });

          response = await uploadMultipleTypes(formData);
          if (response?.success) {
            toast.success(
              `${filesArray.length} document(s) uploaded successfully!`
            );
            onUpload?.(response);
            clearSelection();
          } else throw new Error("Document upload failed");
        } else {
          Array.from(selectedFiles).forEach((file) => {
            formData.append("images", file);
          });

          response = await uploadImages(formData);
          if (response?.success && response?.images?.length) {
            toast.success("Images uploaded successfully!");
            const imageUrls = response.images.map((img) => img.imageUrl);
            if (single) {
              setImage?.(imageUrls[0]);
            } else {
              setImage?.((prev) => [...prev, ...imageUrls]);
            }
            onUpload?.(response);
            clearSelection();
          } else throw new Error("Image upload failed");
        }
      } catch (err) {
        toast.error(err?.message || "Upload failed");
        onUpload?.({ success: false, error: err });
      } finally {
        setUploading(false);
      }
    };

    const handleDeleteUploaded = async (index) => {
      const urlToDelete = image[index];
      const oldImages = [...image]; // For rollback
      try {
        await deleteImage(urlToDelete);
        toast.success("File deleted");
        const updatedImages = image.filter((_, i) => i !== index);
        setImage(updatedImages);

        if (onDelete) {
          if (single) {
            await onDelete(updatedImages[0] || "");
          } else {
            await onDelete(updatedImages);
          }
        }
      } catch (err) {
        setImage(oldImages); // Rollback on error
        toast.error(err.message || "Failed to delete file");
      }
    };

    const handleDeleteLocal = (index) => {
      if (!selectedFiles) return;

      const filesArray = Array.from(selectedFiles).filter(
        (_, i) => i !== index
      );
      const dt = new DataTransfer();
      filesArray.forEach((file) => dt.items.add(file));
      setSelectedFiles(dt.files);

      if (isImageLikeMode && localPreviews[index]) {
        URL.revokeObjectURL(localPreviews[index]);
        setLocalPreviews((prev) => prev.filter((_, i) => i !== index));
      }
    };

    return (
      <div className="relative space-y-1">
        <label className="text-sm font-medium text-black">
          Upload Mode :
          <span className="font-semibold pl-1">
            {isImageMode
              ? "Image(s)"
              : isZipMode
              ? "ZIP File"
              : isTileMode
              ? "Tile Image"
              : isResume
              ? "Resume"
              : isMain
              ? "Main Image"
              : "Document(s)"}
          </span>
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={triggerFileSelect}
            className="flex items-center gap-2 cursor-pointer bg-[#E0E3FF] text-darkblue px-4 py-2 h-10 rounded-md hover:bg-indigo-100 text-sm"
          >
            <UploadCloud className="w-4 h-4" />
            <span>{defaultLabel}</span>
          </button>

          {selectedFiles && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-red-500 hover:text-red-700"
              title="Clear"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Show selected documents (multiple files support) */}
        {isDocumentMode && selectedFiles?.length > 0 && (
          <div className="mt-2 space-y-2">
            <p className="text-xs font-semibold text-gray-600 flex items-center gap-1">
              <FileText className="w-4 h-4" /> Selected Documents (
              {selectedFiles.length})
            </p>
            {Array.from(selectedFiles).map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    Size: {(file.size / (1024 * 1024)).toFixed(2)} MB | Type:{" "}
                    {file.type}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteLocal(index)}
                  className="ml-2 text-red-500 hover:text-red-700"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Show selected ZIP file */}
        {isZipMode && selectedFiles?.length > 0 && (
          <div className="mt-2 p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-800">
            <p>
              <strong>Selected File:</strong> {selectedFiles[0].name}
            </p>
            <p className="text-xs text-gray-500">
              Size: {(selectedFiles[0].size / (1024 * 1024)).toFixed(2)} MB
            </p>
          </div>
        )}

        {isImageLikeMode && localPreviews.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
              <ImagePlus className="w-4 h-4" /> Selected Images (before upload)
            </p>
            <div className="flex flex-wrap gap-2">
              {localPreviews.map((src, index) => (
                <div key={index} className="relative w-16 h-16">
                  <img
                    src={src}
                    alt={`preview-${index}`}
                    className="w-full h-full object-cover rounded-md border"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteLocal(index)}
                    className="absolute -top-1 -right-1 bg-white rounded-full w-6 h-6 flex justify-center items-center shadow-sm hover:bg-gray-200"
                    title="Remove local"
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isImageLikeMode && image?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-600 mt-3 mb-1 flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-600" /> Uploaded Images
            </p>
            <div className="flex flex-wrap gap-2">
              {image.map((imgUrl, index) => (
                <div key={index} className="relative w-16 h-16">
                  <img
                    src={imgUrl}
                    alt={`uploaded-${index}`}
                    className="w-full h-full object-cover rounded-md border"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteUploaded(index)}
                    className="absolute -top-1 -right-1 bg-white rounded-full w-6 h-6 flex justify-center items-center shadow-sm hover:bg-gray-200"
                    title="Delete from server"
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedFiles && (
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            className="mt-2 flex items-center gap-2 bg-indigo-600 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            <Upload className="w-4 h-4" />
            {uploading
              ? "Uploading..."
              : `Upload ${
                  selectedFiles.length > 1
                    ? `${selectedFiles.length} files`
                    : "file"
                }`}
          </button>
        )}

        <input
          type="file"
          accept={
            isImageLikeMode
              ? "image/*"
              : isZipMode
              ? ".zip"
              : ".pdf,.ppt,.pptx,.doc,.docx,.txt"
          }
          onChange={handleChange}
          ref={fileInputRef}
          className="hidden"
          multiple={isImageLikeMode || isDocumentMode}
        />
      </div>
    );
  }
);

export default FileUpload;
